// Status vocabulary, mirroring awards-service's
// internal/services/membership/transitions.go. Reconcile by hand, and reconcile
// in the same change transitions.go moves in: a copy stricter than the server
// hides a legal edit, and a looser one 409s in front of the admin.
//
// Not taken from @tempmee/awards, which carries the wire types and labels but
// no transition matrix at all — and this is its only would-be consumer, since
// the matrix gates the admin status dropdown and no other client has one.
// (Its ESM-only build is not the obstacle it once was: jest.config.js already
// transpiles all of @tempmee. The cost is a package release cycle per edit,
// plus zod@4 as a peer for one form-schema module this repo would never call.)

export const CYCLE_STATUSES = ['pending', 'active', 'complete'];

export const CYCLE_STATUS_LABELS = {
  pending: 'Pending',
  active: 'Active',
  complete: 'Complete',
};

export const MEMBERSHIP_STATUSES = [
  'nominee',
  'applicant',
  'winner',
  'not-selected',
];

/**
 * Not a stored status — it is the absence of a membership row. Selecting it
 * removes the membership (a formal un-entry: the user leaves the cycle's user
 * list), and selecting an entered status from it creates one (a formal entry).
 * The matrix gates both directions, and since GOTU-6889 that includes winner:
 * a winner can be entered straight from here, and removed back to it.
 */
export const NOT_ENTERED = 'not-entered';

export const MEMBERSHIP_STATUS_LABELS = {
  nominee: 'Nominee',
  applicant: 'Applicant',
  winner: 'Winner',
  'not-selected': 'Not Selected',
  'not-entered': 'Not Entered',
};

/** DNT and HYG map to admin profile routes; ADM has no profile page. */
export const USER_TYPE_TO_PROFILE = {
  DNT: 'office',
  HYG: 'professional',
};

/**
 * The server rejects roster-mutating edits — Upload users and per-member
 * status changes — once a cycle is complete. One predicate so the cycle
 * roster and the profile Promotion tab can never disagree about the same
 * cycle's editability.
 */
export const isCycleRosterMutable = (cycleStatus) => cycleStatus !== 'complete';

// Mirrors legalTransitions in awards-service
// internal/services/membership/transitions.go. Winner is an ordinary status
// there, not a terminal one: GOTU-6889 opened six edges around it — in from
// not-entered, nominee and not-selected, out to nominee, not-selected and
// not-entered — so an admin can enter somebody straight at Winner and can
// correct or revoke one from this dropdown rather than only by CSV upload.
// What used to make it terminal was that a win is published to the
// marketplace, but the published entity carries the subject's whole award set
// recomputed from memberships and both server write paths republish it on any
// edge touching winner, so a revocation here clears the badge exactly as a CSV
// upload does.
//
// winner -> applicant is the one edge deliberately left out, and it is the
// same refusal not-selected -> applicant already carries — see
// DECIDED_TO_APPLICANT. not-entered -> applicant exists for the server's
// re-add repair, which can restore applicant on a member whose application
// survived an earlier removal.
export const LEGAL_TRANSITIONS = {
  [NOT_ENTERED]: ['nominee', 'applicant', 'winner'],
  nominee: ['applicant', 'winner', 'not-selected', NOT_ENTERED],
  applicant: ['winner', 'not-selected', NOT_ENTERED],
  'not-selected': ['nominee', 'winner', NOT_ENTERED],
  winner: ['nominee', 'not-selected', NOT_ENTERED],
};

// Winner and Not Selected are both decided outcomes, and Applicant is the
// status the end-of-day feedback gate keys on, so returning a member to it
// re-opens feedback prompts about a result the cycle has already settled. One
// rule, one reason: before GOTU-6889 every target was refused from Winner
// under a single blanket message, and this is the only one left.
const DECIDED_TO_APPLICANT =
  'A decided result cannot return to Applicant — it would re-open feedback about a settled outcome.';

const isDecidedToApplicant = (from, to) =>
  to === 'applicant' && (from === 'winner' || from === 'not-selected');

const OPTION_ORDER = [...MEMBERSHIP_STATUSES, NOT_ENTERED];

/**
 * Every status the select should render, in a fixed order, each carrying
 * whether it is selectable and why not. Returning the full set (rather than
 * filtering) lets the UI grey illegal targets with an explanation instead of
 * silently hiding them.
 */
export const getStatusOptions = (currentStatus) => {
  const legal = LEGAL_TRANSITIONS[currentStatus] ?? [];
  const currentLabel = MEMBERSHIP_STATUS_LABELS[currentStatus] ?? currentStatus;

  return OPTION_ORDER.map((value) => {
    const label = MEMBERSHIP_STATUS_LABELS[value];

    if (value === currentStatus) {
      return { value, label, disabled: true };
    }
    if (legal.includes(value)) {
      return { value, label, disabled: false };
    }

    return {
      value,
      label,
      disabled: true,
      disabledReason: isDecidedToApplicant(currentStatus, value)
        ? DECIDED_TO_APPLICANT
        : `Cannot change from ${currentLabel} to ${label}.`,
    };
  });
};
